import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const PROMPT_TEXTS = {
  simple_pleasures: 'My simple pleasures...',
  geek_out: 'I geek out on...',
  life_goal: 'A life goal of mine...',
  unusual_skill: 'My most unusual skill...',
  green_flags: 'My green flags...',
  perfect_sunday: 'My perfect Sunday...',
  deal_breaker: 'My biggest deal breaker...',
  best_travel: 'Best travel story...',
  dinner_guest: 'Dream dinner guest...',
  hot_take: 'My most controversial opinion...',
  love_language: 'My love language is...',
  pet_peeve: 'My biggest pet peeve...',
  guilty_pleasure: 'My guilty pleasure...',
  two_truths_lie: 'Two truths and a lie...',
  typical_sunday: 'You can find me on a typical Sunday...',
  first_thing_noticed: 'First thing people notice about me...',
  never_do_again: 'Something I will never do again...',
  most_spontaneous: 'Most spontaneous thing I have done...',
  believe_in: 'I believe that...',
  change_mind: 'Something that changed my mind...',
};

const TRAIT_LABELS = {
  smoking: 'Smoking', drinking: 'Drinking', cannabis: 'Cannabis', diet: 'Diet',
  exercise: 'Exercise', sleep_schedule: 'Sleep schedule', education: 'Education',
  religion: 'Religion', politics: 'Political leaning', languages: 'Languages',
  ethnicity: 'Ethnicity', height: 'Height', body_type: 'Body type',
  has_kids: 'Has children', wants_kids: 'Wants children', pets: 'Pets',
  communication_style: 'Communication style', social_energy: 'Social energy',
  relationship_goal: 'Relationship goal', love_language_trait: 'Love language',
};

const styles = {
  container: { padding: '20px' },
  onboardingCard: {
    background: 'linear-gradient(135deg, rgba(232,87,109,0.15), rgba(232,87,109,0.05))',
    border: '1px solid rgba(232,87,109,0.3)',
    borderRadius: 'var(--radius-md)',
    padding: '16px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  onboardingCardText: {
    fontSize: '14px',
    color: 'var(--text-primary)',
    flex: 1,
  },
  onboardingCardBtn: {
    padding: '8px 20px',
    fontSize: '13px',
    borderRadius: 'var(--radius-full)',
    flexShrink: 0,
  },
  photoSection: {
    width: '100%',
    height: '300px',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    background: 'var(--bg-elevated)',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  noPhoto: {
    fontSize: '48px',
    color: 'var(--text-muted)',
  },
  name: {
    fontSize: '24px',
    fontWeight: '700',
  },
  meta: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '8px',
    marginBottom: '16px',
  },
  bio: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    marginBottom: '20px',
  },
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  promptCard: {
    background: 'var(--bg-elevated)',
    borderRadius: 'var(--radius-md)',
    padding: '16px',
    marginBottom: '10px',
  },
  promptQuestion: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    marginBottom: '6px',
  },
  promptAnswer: {
    fontSize: '15px',
    lineHeight: 1.5,
  },
  vaxGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  traitRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid var(--border-color)',
  },
  traitLabel: {
    fontSize: '13px',
    color: 'var(--text-muted)',
  },
  traitValue: {
    fontSize: '13px',
    fontWeight: '500',
  },
  visBadge: {
    fontSize: '10px',
    color: 'var(--text-muted)',
    marginLeft: '6px',
  },
};

export default function ProfileView() {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const age = user.date_of_birth
    ? Math.floor(
        (Date.now() - new Date(user.date_of_birth).getTime()) /
          (365.25 * 24 * 60 * 60 * 1000)
      )
    : null;

  const firstPhoto = user.photos?.[0];

  return (
    <div style={styles.container}>
      {!user.onboarding_complete && (
        <div style={styles.onboardingCard}>
          <span style={styles.onboardingCardText}>
            Complete your voice onboarding to improve your profile and matches
          </span>
          <button
            className="btn btn-primary"
            style={styles.onboardingCardBtn}
            onClick={() => navigate('/onboarding/voice')}
          >
            Resume
          </button>
        </div>
      )}
      <div style={styles.photoSection}>
        {firstPhoto ? (
          <img
            src={`/uploads/${firstPhoto.filename}`}
            alt={user.display_name}
            style={styles.photo}
          />
        ) : (
          <span style={styles.noPhoto}>?</span>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={styles.name}>
            {user.display_name}{age ? `, ${age}` : ''}
          </h2>
          <div style={styles.meta}>
            {user.gender && <span className="badge badge-accent">{user.gender}</span>}
            {user.pronouns && <span className="badge badge-muted">{user.pronouns}</span>}
            {user.orientation && <span className="badge badge-muted">{user.orientation}</span>}
          </div>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/profile/edit')}
        >
          Edit
        </button>
      </div>

      {user.bio && <p style={styles.bio}>{user.bio}</p>}

      {user.prompts?.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Prompts</h3>
          {user.prompts.map((p) => (
            <div key={p.id || p.prompt_key} style={styles.promptCard}>
              <p style={styles.promptQuestion}>
                {PROMPT_TEXTS[p.prompt_key] || p.prompt_key}
              </p>
              <p style={styles.promptAnswer}>{p.response}</p>
            </div>
          ))}
        </div>
      )}

      {user.traits?.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>My Traits</h3>
          {user.traits.map((t) => {
            let display = t.trait_value;
            try { display = JSON.parse(t.trait_value); } catch {}
            if (Array.isArray(display)) display = display.join(', ');
            return (
              <div key={t.trait_key} style={styles.traitRow}>
                <span style={styles.traitLabel}>{TRAIT_LABELS[t.trait_key] || t.trait_key}</span>
                <span>
                  <span style={styles.traitValue}>{display}</span>
                  <span style={styles.visBadge}>
                    {t.visibility === 'private' ? '🔒' : '👁'}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {user.preferences?.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>My Preferences</h3>
          {user.preferences.map((p) => {
            let display = p.pref_value;
            try { display = JSON.parse(p.pref_value); } catch {}
            if (Array.isArray(display)) display = display.join(', ');
            return (
              <div key={p.pref_key} style={styles.traitRow}>
                <span style={styles.traitLabel}>{TRAIT_LABELS[p.pref_key] || p.pref_key}</span>
                <span>
                  <span style={styles.traitValue}>{display}</span>
                  <span style={styles.visBadge}>
                    {p.visibility === 'private' ? '🔒' : '👁'}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {user.vaccinations?.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Vaccination Status</h3>
          <div style={styles.vaxGrid}>
            {user.vaccinations.map((v) => {
              if (v.visibility === 'opted_out') {
                return (
                  <span key={v.vaccine_type} className="badge badge-muted">
                    {v.vaccine_type.replace(/_/g, ' ')} - Opted out
                  </span>
                );
              }
              return (
                <span
                  key={v.vaccine_type}
                  className={`badge ${v.status === 'vaccinated' ? 'badge-success' : v.status === 'partial' ? 'badge-warning' : 'badge-muted'}`}
                >
                  {v.vaccine_type.replace(/_/g, ' ').toUpperCase()}
                  {v.status === 'vaccinated' && ' ✓'}
                  {v.status === 'partial' && ' ~'}
                  {v.visibility === 'private' && ' (private)'}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
